import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { Col, OverlayTrigger, Popover } from "react-bootstrap";
import Tooltip from '@mui/material/Tooltip';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import styles from './Common.module.css';
import { ThemeButton } from '../../components/buttons/ThemeButton';
import NetworkManager from '../../app/managers/networkManager';
import { ProfilePictureEditor } from '../../components/collabs/ProfilePictureEditor';
import { setMyPhoto, removeMyPhoto, setPhotoEditorOpen } from '../../components/collabs/collabsSlice';
import logo from "../../assets/images/logo_main.png";
// Menu glyphs use MUI's icon set instead of the old mismatched PNGs: one flat, resolution
// independent style, with the resting/selected color swap handled by CSS (Common.module.css
// `.menuIcon` / `.selectedMenuButton .menuIcon`) rather than a second baked-in-color asset
// per item.
import DashboardGlyph from '../../components/common/DashboardGlyph';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
// Sub-view glyphs for the "Collabs & Suivis" submenu — the rail is 80px wide when it is
// not hovered, so a sub-item needs an icon of its own to stay readable collapsed.
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import SickRoundedIcon from '@mui/icons-material/SickRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, onPopupHandle, showPopup } from "./commonSlice";
import { logoutUser, resetUserSession } from "../login/loginSlice";
import { useMsal } from "@azure/msal-react";
import { checkPermission, isAdmin, isOwner } from '../../app/utils/helpers';
import Langs, { Localize } from '../../app/lang/langs';
import { confirmDialog } from '../../components/popups/ConfirmDialog';
import { setSelectedTeams } from "../../components/teams/teamsSlice";
import { config, isFeatureHidden } from '../../app/utils/config';
import { MobileMenu } from './MobileMenu';

export function MenuBar() {

    const { instance } = useMsal();
    const popoverRef = useRef()
    const buttonRef = useRef()

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const urlParams = useParams()

    const loginType = useSelector((state) => state.login.loginType)
    const permissions = useSelector((state) => state.login.permissions)
    const confirmationTo = useSelector((state) => state.common.confirmationTo)
    const hasUpdates = useSelector((state) => state.common.hasUpdates)
    const email = useSelector((state) => state.login.userEmail)
    const username = useSelector((state) => state.login.userName)
    const userId = useSelector((state) => state.login.userId)
    const photoUpdatedAt = useSelector((state) => state.login.photoUpdatedAt)
    const photoEditorOpen = useSelector((state) => state.collabs.photoEditorOpen)
    const photoSaving = useSelector((state) => state.collabs.myPhotoSaving)
    const photoError = useSelector((state) => state.collabs.myPhotoError)
    const userRoleId = useSelector((state) => state.login.userRoleId)
    const userRoles = useSelector((state) => state.login.userRoles)
    const auto = useSelector((state) => state.login.auto)
    const idSubcontractor = useSelector((state) => state.login.idSubcontractor)
    const myTeam = useSelector((state) => state.teams.myTeam)
    const teams = useSelector((state) => state.teams.teams)
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds)
    const defaultPage = useSelector((state) => state.common.defaultPage)
    const isMobile = useSelector((state) => state.settings.isMobile)

    const [showPopover, setShowPopover] = useState(false)
    const [menuItems, setMenuItems] = useState(getMenuButtons())
    // Values of the menus whose sub-items are unfolded. Entering a menu unfolds it (see the
    // effect below), but collapsing it from the chevron sticks while the user stays there.
    const [expandedMenus, setExpandedMenus] = useState(urlParams.view ? [urlParams.view] : [])
    // Whether the rail is open (labels) or mini (icons only). Opened from the toggle under
    // the logo, never from hover, and remembered per browser — open until someone collapses
    // it, so a first visit shows what every entry is.
    const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem("sidebarOpen") !== "0")
    // Hover flyout listing a folded group's sub-views, GitLab style: hovering the header
    // pops it out beside the rail, clicking the header folds/unfolds the group inline.
    // `{ value, anchorEl }` of the group it belongs to, or null.
    const [flyout, setFlyout] = useState(null)
    const flyoutCloseTimer = useRef(null)

    function getMenuButtons() {
        return [
            {
                value: 'dashboard',
                label: 'Dashboard',
                // Hand-drawn rather than an MUI icon so the four tiles can animate
                // separately — see DashboardGlyph.js. Same call shape as any MUI icon.
                icon: DashboardGlyph,
                visible: true,
                subView: 0
            },
            {
                value: 'planning',
                label: 'Planning',
                icon: EventNoteRoundedIcon,
                visible: true,
                subView: 0
            },
            {
                value: 'timesheet',
                label: 'Timesheet',
                icon: AccessTimeRoundedIcon,
                visible: auto && myTeam != null,
                subView: 0
            },
            {
                value: 'groupes',
                label: 'Groupes & Collabs',
                icon: GroupsRoundedIcon,
                visible: checkPermission(config.scopes.groups, config.permissionTypes.view, selectedTeamsIds),
                subView: 0,
                // Sub-views of GroupesView, in the rail rather than in a segmented control on
                // the page. "Managers" carries the same three-way edit check the page uses to
                // decide whether to offer that tab at all.
                subItems: [
                    {
                        value: 0,
                        label: 'Groupes',
                        icon: GroupsRoundedIcon,
                        visible: true
                    },
                    {
                        value: 1,
                        label: 'TEAM_COLLABS',
                        icon: FormatListBulletedRoundedIcon,
                        visible: true
                    },
                    {
                        value: 2,
                        label: 'Managers',
                        icon: SupervisorAccountRoundedIcon,
                        visible: selectedTeamsIds?.some(teamId => checkPermission(config.scopes.groups, config.permissionTypes.edit, teamId))
                            && checkPermission(config.scopes.collabs, config.permissionTypes.edit)
                            && checkPermission(config.scopes.clientsTeams, config.permissionTypes.edit)
                    }
                ]
            },
            {
                value: 'collabs',
                label: 'Collabs & Suivis',
                icon: PeopleAltRoundedIcon,
                visible: checkPermission(config.scopes.collabs, config.permissionTypes.view)
                    || checkPermission(config.scopes.warnings, config.permissionTypes.view),
                subView: checkPermission(config.scopes.collabs, config.permissionTypes.view) ? 0 : 1,
                // The page's sub-views live here rather than in a segmented control on the
                // page itself: the rail is the one place the whole navigation is visible,
                // and the list has outgrown a row of tabs. Values mirror the `subView` URL
                // segment CollabsView reads, and the permission checks mirror the ones that
                // build its `viewsOptions`.
                subItems: [
                    {
                        value: 0,
                        label: 'Collabs',
                        icon: FormatListBulletedRoundedIcon,
                        visible: checkPermission(config.scopes.collabs, config.permissionTypes.view)
                    },
                    {
                        value: 1,
                        label: 'Suivis',
                        icon: WarningAmberRoundedIcon,
                        visible: checkPermission(config.scopes.warnings, config.permissionTypes.view)
                    },
                    {
                        value: 2,
                        label: 'SPECIAL_CASES',
                        icon: AssignmentIndRoundedIcon,
                        visible: checkPermission(config.scopes.collabs, config.permissionTypes.view)
                    },
                    {
                        value: 3,
                        label: 'EMPLOYMENT_PROMISES',
                        icon: HandshakeRoundedIcon,
                        visible: checkPermission(config.scopes.collabs, config.permissionTypes.view)
                    }
                ]
            },
            {
                value: 'contracts',
                label: 'Contracts & Missions',
                icon: DescriptionRoundedIcon,
                visible: checkPermission(config.scopes.collabs, config.permissionTypes.view),
                subView: 0,
                // Same arrangement as "Collabs & Suivis": the sub-views live in the rail
                // rather than in a segmented control on the page. Values mirror the `subView`
                // URL segment, and the permission check mirrors ContractsView's own.
                subItems: [
                    {
                        value: 0,
                        label: 'Contracts',
                        icon: AssignmentRoundedIcon,
                        visible: true
                    },
                    {
                        value: 1,
                        label: 'Missions',
                        icon: WorkRoundedIcon,
                        visible: checkPermission(config.scopes.clientsTeams, config.permissionTypes.view)
                    },
                    {
                        value: 2,
                        label: 'Suivis Missions',
                        icon: PlaylistAddCheckRoundedIcon,
                        visible: checkPermission(config.scopes.clientsTeams, config.permissionTypes.view)
                    }
                ]
            },
            // The HR group gathers pages that are each a route of their own, unlike the groups
            // above whose sub-items are sub-views of one page. A sub-item carrying `view` opens
            // that route (remembering its own `subView`), and the group is shown as long as
            // one of them is.
            (() => {
                const hrItems = [
                    {
                        value: 'reports',
                        view: 'reports',
                        label: 'Reports',
                        icon: BarChartRoundedIcon,
                        visible: isOwner(userRoleId) || checkPermission(config.scopes.reports, config.permissionTypes.view),
                        subView: 0
                    },
                    {
                        value: 'evaluations',
                        view: 'evaluations',
                        label: 'Evaluations',
                        icon: FactCheckRoundedIcon,
                        visible: checkPermission(config.scopes.evaluations, config.permissionTypes.view, selectedTeamsIds),
                        subView: 0
                    },
                    {
                        value: 'ventr',
                        view: 'ventr',
                        label: 'V-Entr',
                        icon: ApartmentRoundedIcon,
                        visible: checkPermission(config.scopes.ventr, config.permissionTypes.view) || !idSubcontractor,
                        subView: 0
                    },
                    {
                        value: 'clients',
                        view: 'clients',
                        label: 'CLIENTS_TEAMS',
                        icon: BusinessCenterRoundedIcon,
                        visible: checkPermission(config.scopes.clientsTeams, config.permissionTypes.view),
                        subView: 0
                    },
                    {
                        value: 'medicalVisits',
                        view: 'medicalVisits',
                        label: 'MEDICAL_VISITS',
                        icon: LocalHospitalRoundedIcon,
                        visible: checkPermission(config.scopes.medicalVisits, config.permissionTypes.view),
                        subView: 0
                    },
                    {
                        value: 'opportunities',
                        view: 'opportunities',
                        label: 'OPPORTUNITIES',
                        icon: SwapHorizRoundedIcon,
                        visible: checkPermission(config.scopes.opportunities, config.permissionTypes.view),
                        subView: 0
                    },
                    {
                        value: 'vreference',
                        view: 'vreference',
                        label: 'VREFERENCE_TITLE',
                        icon: PersonAddAlt1RoundedIcon,
                        // Open to everyone: any collab may refer someone they know. The
                        // `vreference` scope only upgrades the user from "my contacts" to "the
                        // whole directory".
                        visible: !isFeatureHidden('vreference'),
                        subView: 0
                    },
                    {
                        value: 'vresume',
                        view: 'vresume',
                        label: 'VRESUME_TITLE',
                        icon: ArticleRoundedIcon,
                        // Open to everyone: every collab owns their own CV. The `vresume` scope
                        // only widens the user from "my CV" to their teams' or every team's.
                        visible: !isFeatureHidden('vresume'),
                        subView: 0
                    }
                ]
                return {
                    value: 'hr',
                    label: 'MENU_HR',
                    icon: BadgeRoundedIcon,
                    visible: hrItems.some(sub => sub.visible),
                    subView: 0,
                    subItems: hrItems
                }
            })(),
            {
                value: 'leaves',
                label: 'LEAVES_AND_ABSENCES',
                // A calendar with a day struck out: covers congés and absences alike, where
                // the parasol only said "holiday". The parasol stays on the Leaves sub-item.
                icon: EventBusyRoundedIcon,
                visible: true,
                subView: 0,
                // Values mirror the `subView` URL segment LeavesAndAbsencesView reads.
                subItems: [
                    {
                        value: 0,
                        label: 'Leaves',
                        icon: BeachAccessRoundedIcon,
                        visible: true
                    },
                    {
                        value: 1,
                        // Open to everyone: holders of the `workStoppages` scope get the
                        // company-wide suivi, every other collab their own arrêts only.
                        label: 'WORK_STOPPAGES',
                        icon: SickRoundedIcon,
                        visible: true
                    }
                ]
            },
            // Training group: the three pages that are about what a collab knows rather than
            // what they are scheduled to do — the skills framework (V-Comp), the course
            // catalogue (V-Learning) and the security awareness campaigns (V-Sécurité).
            // Routed sub-items like the HR group above, so each keeps its own top-level route.
            (() => {
                const trainingItems = [
                    {
                        value: 'vcomp',
                        view: 'vcomp',
                        label: 'V-Comp',
                        icon: WorkspacePremiumRoundedIcon,
                        visible: checkPermission(config.scopes.vcomp, config.permissionTypes.view),
                        subView: 0
                    },
                    {
                        value: 'vlearn',
                        view: 'vlearn',
                        // A plain lang key like every other entry: `renderRow` already wraps
                        // `entry.label` in <Langs>, so handing it a <Langs> element nested one
                        // inside the other for no gain.
                        label: 'VLEARN_TITLE',
                        // An MUI icon like every other entry now. This was two baked-colour SVGs,
                        // which is the pattern the icon refactor removed — and since the render reads
                        // `item.icon`, the old shape would have drawn nothing at all.
                        icon: SchoolRoundedIcon,
                        // Open to everyone, like V-Resume, V-Reference and V-Sécurité: every collab
                        // has courses assigned to them. The `vlearn` scope does not grant entry — it
                        // widens the user from "the courses assigned to me" to their teams'
                        // catalogue and training dashboard (view), authoring for those teams (edit),
                        // or for any team (edit_all).
                        visible: !isFeatureHidden('vlearn'),
                        subView: 0
                    },
                    {
                        value: 'vsecurite',
                        view: 'vsecurite',
                        label: 'VSEC_VIEW_TITLE',
                        icon: ShieldRoundedIcon,
                        visible: true,
                        subView: 0
                    }
                ]
                return {
                    value: 'training',
                    label: 'MENU_TRAINING',
                    icon: MenuBookRoundedIcon,
                    // V-Learning and V-Sécurité are open to everyone, so in practice this group
                    // always shows; the check keeps it honest if either is ever gated.
                    visible: trainingItems.some(sub => sub.visible),
                    subView: 0,
                    subItems: trainingItems
                }
            })(),
            // Routed sub-items, like the HR group. Settings is open to everyone, so the group
            // always shows; permissions only for admins.
            {
                value: 'administration',
                label: 'Settings',
                icon: ManageAccountsRoundedIcon,
                visible: true,
                subView: 0,
                subItems: [
                    {
                        value: 'permissions',
                        view: 'permissions',
                        label: 'PERMISSIONS_MANAGEMENT',
                        icon: AdminPanelSettingsRoundedIcon,
                        visible: userRoleId == 1,
                        subView: 0
                    },
                    {
                        value: 'settings',
                        view: 'settings',
                        label: 'Settings',
                        icon: SettingsRoundedIcon,
                        visible: true,
                        subView: 0
                    }
                ]
            }
        ]
    }

    useEffect(() => {
        setMenuItems(getMenuButtons())
    }, [teams, myTeam, userRoleId, auto, permissions, selectedTeamsIds])

    // The group holding a sub-item that is a route of its own (one with `view`), if any.
    function findGroupOfView(view) {
        return menuItems.find(item => (item.subItems || []).some(sub => sub.view && sub.view === view))
    }

    // The entry that owns a route: a top-level menu, or a routed sub-item of a group.
    function findRouteEntry(view) {
        const item = menuItems.find(item => item.value === view)
        if (item) return item
        const group = findGroupOfView(view)
        return group && group.subItems.find(sub => sub.view === view)
    }

    function isSubItemSelected(item, sub) {
        return sub.view
            ? urlParams.view == sub.view
            : (urlParams.view == item.value && urlParams.subView == sub.value)
    }

    function openSubItem(item, sub) {
        if (sub.view) setSelectedMenu(sub.view)
        else setSelectedSubMenu(item.value, sub.value)
    }

    useEffect(() => {
        if (confirmationTo === "CONFIRM_LOGOUT") {
            dispatch(onPopupHandle())
            dispatch(setIsLoading(true))
            if (loginType === "MS") {
                const logoutRequest = {
                    account: instance.getAccountByUsername(email)
                }
                instance.logout(logoutRequest)
            }
            dispatch(logoutUser())
            dispatch(resetUserSession({ doReload: true }))
        }
    }, [confirmationTo])

    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
                setShowPopover(false)
            }
        }
        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [popoverRef])

    useEffect(() => {
        if (urlParams.view) {
            const entry = findRouteEntry(urlParams.view)
            if (entry) entry.subView = urlParams.subView
            setMenuItems([...menuItems])
        }
    }, [urlParams])

    // Entering a menu unfolds its sub-items, so landing on a page always shows where in it
    // you are. Keyed on the menu alone: moving between sub-views does not re-open a list the
    // user has just folded away.
    useEffect(() => {
        if (urlParams.view) {
            const key = findGroupOfView(urlParams.view)?.value || urlParams.view
            setExpandedMenus(expanded => expanded.includes(key)
                ? expanded
                : [...expanded, key])
        }
    }, [urlParams.view])

    // The page content sits beside the rail, not under it, so its own width comes from this
    // variable. Zero on phones, where MobileMenu replaces the rail entirely.
    useEffect(() => {
        localStorage.setItem("sidebarOpen", sidebarOpen ? "1" : "0")
        const width = isMobile == 2 ? "0px" : (sidebarOpen ? "240px" : "80px")
        document.documentElement.style.setProperty("--sidebar-w", width)
    }, [sidebarOpen, isMobile])

    useEffect(() => () => clearTimeout(flyoutCloseTimer.current), [])

    function openFlyout(value, anchorEl) {
        clearTimeout(flyoutCloseTimer.current)
        setFlyout({ value, anchorEl })
    }

    // Delayed so the pointer can cross the gap between the header and the flyout.
    function scheduleFlyoutClose() {
        clearTimeout(flyoutCloseTimer.current)
        flyoutCloseTimer.current = setTimeout(() => setFlyout(null), 150)
    }

    function cancelFlyoutClose() {
        clearTimeout(flyoutCloseTimer.current)
    }

    function closeFlyout() {
        clearTimeout(flyoutCloseTimer.current)
        setFlyout(null)
    }

    function toggleMenuExpanded(value) {
        setExpandedMenus(expanded => expanded.includes(value)
            ? expanded.filter(v => v !== value)
            : [...expanded, value])
    }

    const handleShowPopover = () => {
        setShowPopover(!showPopover)
    }

    function handleLogout() {
        dispatch(showPopup({ show: true, title: "CONFIRM_LOGOUT", message: "CONFIRM_LOGOUT_MSG" }))
    }

    function setSelectedMenu(value) {
        // Opening a menu from its own row unfolds it again, so a list the user folded away
        // is never left hidden under the page they just asked for.
        const key = findGroupOfView(value)?.value || value
        setExpandedMenus(expanded => expanded.includes(key) ? expanded : [...expanded, key])
        const subview = findRouteEntry(value)?.subView || 0
        let selectedTeamId = selectedTeamsIds[0]
        if (!selectedTeamId) {
            if (myTeam) selectedTeamId = myTeam.id
            else if (teams && teams[0]) selectedTeamId = teams[0].id
            if (selectedTeamId)
                dispatch(setSelectedTeams([selectedTeamId]))
        }
        navigate(`/${value}/${selectedTeamId}/${subview}`)
    }

    // Jumps straight to one sub-view of a menu, remembering it on the item so returning to
    // the menu from elsewhere lands on the same sub-view — the behaviour `subView` already
    // had when the page's own tab bar set it.
    function setSelectedSubMenu(value, subView) {
        menuItems.forEach(item => {
            if (item.value === value) {
                item.subView = subView
            }
        })
        setMenuItems([...menuItems])
        setSelectedMenu(value)
    }

    function getUsernameInitials() {
        if (username && username.length && username !== "null") {
            let array = username.split(" ")
            let initials = ""
            while (initials.length < 2 && array.length > 0) {
                let word = array.pop()
                initials += word.slice(0, 1)
            }
            return initials.toUpperCase()
        } else if (email) {
            return email.slice(0, 1).toUpperCase()
        }
    }

    // Null when the signed-in collab has no picture, which is what makes the button fall
    // back to initials. Versioned by photoUpdatedAt, so a new upload replaces it at once
    // while an unchanged one is served from cache.
    const myPhotoUrl = NetworkManager.getInstance().getCollabPhotoUrl(userId, photoUpdatedAt)

    function getRoleType() {
        if (typeof userRoleId != 'undefined')
            switch (parseInt(userRoleId)) {
                case 1:
                    return "Admin"
                case 2:
                    return "Manager"
                case 3:
                    return "Collab"
                default:
                    break;
            }
    }
    if (isMobile == 2) {
        return (
            <>
                <MobileMenu
                    // The whole tree, groups intact. The sheet used to be handed a flattened
                    // list and laid the lot out as one 24-card grid, so "Training & Skills"
                    // and "HR" existed on the rail and nowhere else. MobileMenu turns a group
                    // of routed pages into a titled section of the same cards instead.
                    menuItems={menuItems}
                    urlParams={urlParams}
                    email={email}
                    username={username}
                    roleType={getRoleType()}
                    getUsernameInitials={getUsernameInitials}
                    photoUrl={myPhotoUrl}
                    onEditPhoto={() => dispatch(setPhotoEditorOpen(true))}
                    onSelectMenu={setSelectedMenu}
                    onLogout={handleLogout}
                />
                {/* The rail's copy of this sits past the early return, so on a phone the
                    picture editor had no mount point at all and the sheet's avatar was a
                    button that did nothing. */}
                {photoEditorOpen ? (
                    <ProfilePictureEditor
                        open={photoEditorOpen}
                        currentPhotoUrl={myPhotoUrl}
                        saving={photoSaving}
                        error={photoError}
                        onClose={() => dispatch(setPhotoEditorOpen(false))}
                        onSave={(dataUrl) => dispatch(setMyPhoto(dataUrl))}
                    />
                ) : null}
            </>
        )
    }

    // One row of the rail. `sub` is set for a sub-view row, which differs only in size,
    // indent and what the click does. In mini no label is rendered at all — the name of the
    // row arrives as a tooltip instead.
    function renderRow({ item, sub, hasSubItems, expanded, index }) {
        const isSub = !!sub
        const entry = sub || item
        const Icon = entry.icon
        const isCurrentMenu = urlParams.view == item.value || findGroupOfView(urlParams.view) === item
        // For a group header this marks "the page you are on lives in here" — only while it is
        // folded: unfolded, the selected sub-view row is the one that carries the highlight.
        const isSelected = isSub
            ? isSubItemSelected(item, sub)
            : (isCurrentMenu && !(hasSubItems && expanded))
        // A folded group shows its sub-views in the hover flyout instead of a tooltip.
        const hasFlyout = !isSub && hasSubItems && !expanded

        const button = (
            <button className={styles.menuButton + " " +
                (isSub ? styles.subMenuButton + " " : "") +
                (hasSubItems ? styles.groupButton + " " : "") +
                (isSelected ? styles.selectedMenuButton : "")}
                aria-expanded={hasSubItems ? expanded : undefined}
                aria-label={hasSubItems ? Localize(expanded ? "MENU_COLLAPSE" : "MENU_EXPAND") : undefined}
                onClick={() => {
                    if (isSub) openSubItem(item, sub)
                    // A menu that has sub-views is a group header, not a destination of its
                    // own: the whole row folds and unfolds it, and the sub-views are what
                    // open a page.
                    else if (hasSubItems) {
                        closeFlyout()
                        toggleMenuExpanded(item.value)
                    }
                    else setSelectedMenu(item.value)
                }}>
                <span className={styles.menuIcon + (isSub ? " " + styles.subMenuIcon : "")}>
                    <Icon style={{ fontSize: isSub ? 22 : 30 }} />
                    {item.value === 'dashboard' && hasUpdates ? (
                        <span className={styles.updateDot} />
                    ) : null}
                </span>
                {sidebarOpen ? (
                    <span className={styles.label + (isSub ? " " + styles.subLabel : "")}>
                        <Langs str={entry.label} />
                    </span>
                ) : null}
                {/* Which way the group is folded — a marker, not a control. It lives inside
                    the row's own button so that the arrow is part of the same hit target as
                    the rest of the row. Open, it is the chevron at the end of the row; in
                    mini it shrinks to a caret in the corner. */}
                {hasSubItems ? (
                    <span className={sidebarOpen ? styles.expandIndicator : styles.miniCaret}>
                        <ChevronRightRoundedIcon
                            style={{
                                fontSize: sidebarOpen ? 22 : 15,
                                // Points right while folded (the flyout opens that way) and
                                // turns down once the group unfolds inline, GitLab style.
                                transform: expanded ? "rotate(90deg)" : "none",
                                transition: "transform 0.25s ease"
                            }} />
                    </span>
                ) : null}
            </button>
        )

        return <div className={styles.menuItem +
            (isSub ? " " + styles.subMenuItem : "") +
            (hasSubItems ? " " + styles.groupItem : "") +
            (hasSubItems && expanded ? " " + styles.groupItemExpanded : "")}
            key={isSub ? "menu_" + item.value + "_" + sub.value : "menu_" + item.value}
            // Sub-views come in one after another as the group unfolds, rather than all at
            // once. The delay is what staggers them; the animation itself is in the CSS.
            style={isSub ? { animationDelay: `${index * 35}ms` } : undefined}
            onMouseEnter={hasFlyout ? (e) => openFlyout(item.value, e.currentTarget) : undefined}
            onMouseLeave={hasFlyout ? scheduleFlyoutClose : undefined}>
            {sidebarOpen || hasFlyout ? button : (
                <Tooltip title={<Langs str={entry.label} />} placement="right" arrow>
                    {button}
                </Tooltip>
            )}
        </div>
    }

    return <Col className={styles.menuBar + (sidebarOpen ? " " + styles.menuBarOpen : "")}>
        <img className={styles.headerLogo}
            src={logo}
            alt={"logo"}
            onClick={() => setSelectedMenu(defaultPage)} />
        {(() => {
            const railButton = (
                <button className={styles.railToggle}
                    aria-label={Localize(sidebarOpen ? "MENU_HIDE" : "MENU_SHOW")}
                    aria-expanded={sidebarOpen}
                    onClick={() => setSidebarOpen(!sidebarOpen)}>
                    <span className={styles.railGlyph}>
                        {sidebarOpen
                            ? <MenuOpenRoundedIcon style={{ fontSize: 26 }} />
                            : <MenuRoundedIcon style={{ fontSize: 26 }} />}
                    </span>
                    {/* Named while the rail is open, so the way back to the mini rail is not
                        left to a glyph. Mini stays icons only — the tooltip carries it. */}
                    {sidebarOpen ? (
                        <span className={styles.railLabel}>
                            <Langs str="MENU_HIDE" />
                        </span>
                    ) : null}
                </button>
            )
            return sidebarOpen ? railButton : (
                <Tooltip title={<Langs str="MENU_SHOW" />} placement="right" arrow>
                    {railButton}
                </Tooltip>
            )
        })()}
        <div className={styles.menuList}>
            {
                menuItems.filter(item => item.visible).map(item => {
                    const subItems = (item.subItems || []).filter(sub => sub.visible)
                    const hasSubItems = subItems.length > 0
                    const expanded = expandedMenus.includes(item.value)
                    return <React.Fragment key={"row_" + item.value}>
                        {renderRow({ item, hasSubItems, expanded })}
                        {/* The sub-views stay mounted and the wrapper animates its height,
                            so a group folds shut as well as opening — a run of rows that is
                            unmounted on collapse can only ever animate one way. */}
                        {hasSubItems ? (
                            <div className={styles.subMenuGroup +
                                (expanded ? " " + styles.subMenuGroupOpen : "")}>
                                <div className={styles.subMenuGroupInner}>
                                    {subItems.map((sub, index) => renderRow({ item, sub, index }))}
                                </div>
                            </div>
                        ) : null}
                    </React.Fragment>
                })
            }
        </div>
        {(() => {
            const flyoutItem = flyout && menuItems.find(item => item.value === flyout.value && item.visible)
            if (!flyoutItem || expandedMenus.includes(flyoutItem.value)) return null
            const subItems = (flyoutItem.subItems || []).filter(sub => sub.visible)
            return (
                <Popper open
                    anchorEl={flyout.anchorEl}
                    placement="right-start"
                    style={{ zIndex: 1300 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                    onMouseEnter={cancelFlyoutClose}
                    onMouseLeave={scheduleFlyoutClose}>
                    <Paper className={styles.flyout} elevation={0}>
                        <div className={styles.flyoutTitle}>
                            <Langs str={flyoutItem.label} />
                        </div>
                        <MenuList dense disablePadding>
                            {subItems.map(sub => {
                                const SubIcon = sub.icon
                                return (
                                    <MenuItem key={"flyout_" + flyoutItem.value + "_" + sub.value}
                                        className={styles.flyoutItem}
                                        selected={isSubItemSelected(flyoutItem, sub)}
                                        onClick={() => {
                                            closeFlyout()
                                            openSubItem(flyoutItem, sub)
                                        }}>
                                        <ListItemIcon className={styles.flyoutIcon}>
                                            <SubIcon style={{ fontSize: 20 }} />
                                        </ListItemIcon>
                                        <ListItemText primary={<Langs str={sub.label} />} />
                                    </MenuItem>
                                )
                            })}
                        </MenuList>
                    </Paper>
                </Popper>
            )
        })()}
        <div className={styles.profileMenuFooter}>
            <OverlayTrigger
                show={showPopover}
                key={'top'}
                placement={'top'}
                overlay={
                    <Popover id='popover-positioned-top' style={{ zIndex: 1200, minWidth: '220px', border: 'none', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.14)' }}>
                        <Popover.Body ref={popoverRef} style={{ padding: 0 }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #1a3c6e 0%, #2a6db5 100%)',
                                padding: '16px 16px 14px'
                            }}>
                                {/* Profile picture, with the control to change it. Clicking
                                    opens the crop dialog, which is mounted outside this
                                    popover — the popover closes on outside mousedown, and a
                                    dialog rendered in here would be unmounted with it. */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                                    <div
                                        onClick={() => dispatch(setPhotoEditorOpen(true))}
                                        title={Localize('PHOTO_EDIT_TITLE')}
                                        style={{
                                            width: '56px',
                                            height: '56px',
                                            borderRadius: '50%',
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            position: 'relative',
                                            background: myPhotoUrl ? 'transparent' : 'rgba(255,255,255,0.22)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '18px',
                                            overflow: 'hidden',
                                            border: '2px solid rgba(255,255,255,0.35)',
                                        }}
                                    >
                                        {myPhotoUrl
                                            ? <img src={myPhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            : getUsernameInitials()}
                                    </div>
                                    <div>
                                        <button
                                            onClick={() => dispatch(setPhotoEditorOpen(true))}
                                            style={{
                                                background: 'rgba(255,255,255,0.2)',
                                                border: 'none',
                                                color: 'white',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                padding: '5px 12px',
                                                borderRadius: '20px',
                                                cursor: 'pointer',
                                                display: 'block',
                                            }}
                                        >
                                            <Langs str={photoUpdatedAt ? "PHOTO_CHANGE_FILE" : "PHOTO_ADD"} />
                                        </button>
                                        {photoUpdatedAt ? (
                                            <button
                                                onClick={() => confirmDialog({
                                                    title: <Langs str="PHOTO_REMOVE" />,
                                                    message: <Langs str="PHOTO_REMOVE_MINE_CONFIRM" />,
                                                    danger: true,
                                                    confirmLabel: <Langs str="PHOTO_REMOVE" />,
                                                    onConfirm: () => dispatch(removeMyPhoto()),
                                                })}
                                                disabled={photoSaving}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'rgba(255,255,255,0.75)',
                                                    fontSize: '11px',
                                                    padding: '4px 0 0 12px',
                                                    cursor: photoSaving ? 'default' : 'pointer',
                                                    textDecoration: 'underline',
                                                    display: 'block',
                                                }}
                                            >
                                                <Langs str="PHOTO_REMOVE" />
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                                {username && username !== "null" && (
                                    <div style={{ fontWeight: '700', fontSize: '15px', color: 'white', marginBottom: '4px' }}>
                                        {username}
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {(userRoles && userRoles.length ? userRoles : [getRoleType()]).map((role, i) => (
                                        <span key={i} style={{
                                            background: 'rgba(255,255,255,0.22)',
                                            color: 'white',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            padding: '2px 10px',
                                            borderRadius: '20px',
                                            letterSpacing: '0.5px',
                                            display: 'inline-block'
                                        }}>
                                            {role}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div style={{ padding: '12px 16px 16px' }}>
                                <div style={{ fontSize: '12px', color: '#777', marginBottom: '12px', wordBreak: 'break-all' }}>
                                    {email}
                                </div>
                                <ThemeButton
                                    title="LOGOUT"
                                    active={true}
                                    onClick={() => handleLogout()}
                                />
                            </div>
                        </Popover.Body>
                    </Popover>
                }
            >
                <button
                    className={styles.userButton}
                    ref={buttonRef}
                    onClick={handleShowPopover}
                    // The gradient is the initials background; with a photo it would show as
                    // a rim around the circle, so it is dropped when one is present.
                    style={myPhotoUrl ? { background: 'none', padding: 0, overflow: 'hidden' } : undefined}>
                    {myPhotoUrl ? (
                        <img
                            src={myPhotoUrl}
                            alt={username && username !== "null" ? username : ""}
                            // Falls back to the initials if the picture ever fails to load,
                            // rather than leaving a broken-image icon in the menu.
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    ) : getUsernameInitials()}
                </button>
            </OverlayTrigger>
        </div>
        {photoEditorOpen ? (
            <ProfilePictureEditor
                open={photoEditorOpen}
                currentPhotoUrl={myPhotoUrl}
                saving={photoSaving}
                error={photoError}
                onClose={() => dispatch(setPhotoEditorOpen(false))}
                onSave={(dataUrl) => dispatch(setMyPhoto(dataUrl))}
            />
        ) : null}
    </Col>
}